import {
  Component,
  Directive,
  HostBinding,
  ViewEncapsulation,
  booleanAttribute,
  computed,
  input,
  numberAttribute
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { UmssSkeletonComponent } from '../skeleton/skeleton.component';

@Directive({ selector: '[comparison-label]', standalone: true })
export class ComparisonLabelDirective {}

@Directive({ selector: '[comparison-card]', standalone: true })
export class ComparisonCardDirective {}

/**
 * `umss-comparison-layout` — Side-by-side comparison table.
 *
 * Renders a sticky labels column on the left and a horizontally scrollable
 * set of cards on the right. When `[loading]` is true, skeleton blocks are
 * shown automatically — no extra template logic needed in the consumer.
 *
 * ── Basic usage ──────────────────────────────────────────────────────────
 *
 * ```html
 * <umss-comparison-layout [labelsWidth]="160">
 *   <!-- One [comparison-label] per row label -->
 *   <div comparison-label>Credentials</div>
 *   <div comparison-label>Years of experience</div>
 *   <div comparison-label>Rating</div>
 *
 *   <!-- One <umss-info-card comparison-card> per column -->
 *   @for (item of items(); track item.id) {
 *     <umss-info-card comparison-card>
 *       <header info-card-header>...</header>
 *       <div info-card-item>...</div>   <!-- one per label row -->
 *       <footer info-card-footer>...</footer>
 *     </umss-info-card>
 *   }
 * </umss-comparison-layout>
 * ```
 *
 * ── With skeleton loading ─────────────────────────────────────────────────
 *
 * Pass `[loading]`, `[labelRowCount]` (= number of comparison-label items),
 * and `[skeletonCardCount]` (how many skeleton cards to show).
 * The component renders placeholder skeletons automatically while loading.
 *
 * ```html
 * <umss-comparison-layout
 *   [labelsWidth]="160"
 *   [loading]="loading()"
 *   [labelRowCount]="3"
 *   [skeletonCardCount]="3"
 * >
 *   <div comparison-label>Credentials</div>
 *   <div comparison-label>Years of experience</div>
 *   <div comparison-label>Rating</div>
 *
 *   @for (item of items(); track item.id) {
 *     <umss-info-card comparison-card>...</umss-info-card>
 *   }
 * </umss-comparison-layout>
 * ```
 *
 * ── Inputs ───────────────────────────────────────────────────────────────
 * | Input              | Default | Description                            |
 * |--------------------|---------|----------------------------------------|
 * | labelsWidth        | 134     | Labels column width in px              |
 * | headerSpacerHeight | 161     | Header area height in px (aligns rows) |
 * | loading            | false   | Show skeleton placeholders             |
 * | labelRowCount      | 4       | Rows count (used for skeleton height)  |
 * | skeletonCardCount  | 3       | Number of skeleton cards while loading |
 * | maxCardWidth       | 300     | Maximum width of comparison cards in px|
 */

// px height of each label/item row (matches var(--spacing-11))
const ROW_HEIGHT_PX = 44;
// px height of the info-card footer area
const FOOTER_HEIGHT_PX = 72;

@Component({
  selector: 'umss-comparison-layout',
  standalone: true,
  imports: [CommonModule, UmssSkeletonComponent],
  templateUrl: './comparison-layout.component.html',
  styleUrls: ['./comparison-layout.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class UmssComparisonLayoutComponent {
  @HostBinding('style.display') display = 'contents';

  // height in px that the spacer should occupy to align labels with card body rows
  headerSpacerHeight = input(161, { transform: numberAttribute });

  // width in px of the labels column
  labelsWidth = input(134, { transform: numberAttribute });

  // skeleton loading state
  loading = input(false, { transform: booleanAttribute });

  // how many label rows to use for skeleton height calculation
  labelRowCount = input(4, { transform: numberAttribute });

  // how many card skeletons to show while loading
  skeletonCardCount = input(3, { transform: numberAttribute });

  // maximum width of comparison cards (px)
  maxCardWidth = input(250, { transform: numberAttribute });

  readonly skeletonCards = computed(() =>
    Array.from({ length: this.skeletonCardCount() }, (_, i) => i)
  );

  readonly skeletonLabelPanelStyles = computed(() => ({
    height: `${this.labelRowCount() * ROW_HEIGHT_PX}px`
  }));

  readonly skeletonCardStyles = computed(() => ({
    height: `${this.headerSpacerHeight() + this.labelRowCount() * ROW_HEIGHT_PX + FOOTER_HEIGHT_PX}px`,
    'min-width': '200px',
    'max-width': `${this.maxCardWidth()}px`,
    flex: '1'
  }));

  get spacerStyle(): Record<string, string> {
    return { height: `${this.headerSpacerHeight()}px` };
  }

  get labelsStyle(): Record<string, string> {
    return { width: `${this.labelsWidth()}px` };
  }

  get cardStyles(): Record<string, string> {
    return { 'max-width': `${this.maxCardWidth()}px` };
  }
}
